/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle
} from 'react';
import { isSafari } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import { Popover } from '@barges/react';
import { analyticsService } from '@common/analytics';
import classes from './classes.module.scss';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import {
  isMobile, getSectionForEvent, getClickSourceForEvent
} from '../../config/util';
import VideoPlayer from '../video_player';
import { SvgIcon } from '../../../common/sprites';
import { isKA, paramOrNul } from '../../../common/config/utils';

export const SCREEN_THEME = {
  HOME: 'HOME',
  PROFILE: 'PROFILE'
};

const xIcon = require('./img/x_icon.png');

const isInFullScreenForBrowserCheck = () => (
  (document.webkitFullscreenElement && document.webkitFullscreenElement !== null)
  || (document.mozFullScreenElement && document.mozFullScreenElement !== null)
  || (document.msFullscreenElement && document.msFullscreenElement !== null)
);

const isInFullScreen = () => (
  (document.fullscreenElement && document.fullscreenElement !== null)
  || isInFullScreenForBrowserCheck()
);

const requestFullScreenHandler = ({ video }) => {
  if (video.requestFullScreen) {
    // W3C API
    video.requestFullScreen();
  } else if (video.mozRequestFullScreen) {
    // Mozilla current API
    video.mozRequestFullScreen();
  } else if (video.webkitRequestFullScreen) {
    // Webkit current API
    video.webkitRequestFullScreen();
  } else if (video.webkitEnterFullscreen) {
    // This is the iOS Mobile edge case
    video.webkitEnterFullscreen();
  }
};

const onPlayingHandler = ({ videoRef }) => {
  const video = videoRef.current;
  if (!isMobile()) return;
  if (!isInFullScreen() && !video.paused) {
    requestFullScreenHandler({ video });
  }
};

const getReactPlayerContainerStyle = () => {
  if (isMobile()) return { '--reactPlayerContainerWidth': '100%', '--reactPlayerContainerHeight': '100%' };
  return { '--reactPlayerContainerWidth': '50%', '--reactPlayerContainerHeight': '50%' };
};

const getCloseButtonTopStyle = () => {
  if (isMobile()) return { '--closeButtonTop': '10px', '--closeButtonRight': '10px' };
  return { '--closeButtonTop': '-18px', '--closeButtonRight': '-25px' };
};

const getPlayBtn = ({ playImg }) => {
  if (playImg) return playImg;
  return <SvgIcon id="profile_play" className={ isKA() ? classes.playButtonimgKA : classes.playButtonimg } />;
};

const setMutedForSafari = ({ playerVisible, setMuted }) => {
  if (playerVisible && isSafari && !isMobile()) {
    setMuted(true);
  }
};

const trackPlayVideoEvent = ({
  videoRef, needTrackProfileVideoEvent, advisorId, location, analytics
}) => {
  const videoPlayingDuration = videoRef.current.currentTime;
  if (needTrackProfileVideoEvent && videoPlayingDuration >= 4) {
    const { clickSource } = getClickSourceForEvent({ location });
    const { section } = getSectionForEvent({ location });

    const properties = {
      'advisor id': advisorId,
      'click source': clickSource.toLowerCase(),
      ...paramOrNul('section', section),
      'play time': parseFloat(videoPlayingDuration).toFixed(),
      ...analytics
    };
    analyticsService.trackEvent('Playing advisor profile video', properties);
  }
};

const onFullScreenChangeHandler = ({ e, onClose }) => {
  if (isMobile() && !isInFullScreen()) onClose(e);
};

const AdvisorVideoPlayer = forwardRef(({
  profileVideoUrl,
  advisorId,
  needTrackProfileVideoEvent = false,
  playImg,
  analytics
}, ref) => {
  const [playerVisible, setPlayerVisible] = useState(false);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef();
  const closeBtnRef = useRef();
  const playerContentRef = useRef();
  const location = useLocation();

  const onPlayerClick = (e) => {
    e.stopPropagation();
  };

  const onClose = (e) => {
    log(LOG_LEVELS.INFO, 'Player', 'on close');
    e.stopPropagation();
    setMuted(false);
    trackPlayVideoEvent({
      videoRef,
      needTrackProfileVideoEvent,
      advisorId,
      location,
      analytics
    });
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    setPlayerVisible(false);
  };

  const onFullScreenChange = (e) => {
    onFullScreenChangeHandler({ e, onClose });
  };

  useEffect(() => {
    setMutedForSafari({ playerVisible, setMuted });
    const video = videoRef.current;
    if (playerVisible && video) {
      video.play();
      if (isMobile()) {
        // Webkit
        video.onfullscreenchange = onFullScreenChange;
        video.onwebkitfullscreenchange = onFullScreenChange;
        // iOS Mobile
        video.addEventListener('webkitendfullscreen', onFullScreenChange, false);
        return () => video.removeEventListener('webkitendfullscreen', onFullScreenChange, false);
      }
      const playerContent = playerContentRef.current;
      const closeBtn = closeBtnRef.current;
      if (playerContent) {
        playerContent.addEventListener('click', onClose, false);
        closeBtn.addEventListener('click', onClose, false);
        video.addEventListener('click', onPlayerClick, false);
        return () => {
          playerContent.removeEventListener('click', onClose, false);
          closeBtn.removeEventListener('click', onClose, false);
          video.removeEventListener('click', onPlayerClick, false);
        };
      }
    }
    return () => { };
  }, [playerVisible]);

  const showPlayerVisible = () => {
    log(LOG_LEVELS.INFO, 'Player', 'on set Player Visible');
    analyticsService.trackPageVisit('media player');
    if (!profileVideoUrl) return;
    setPlayerVisible(true);
  };

  useImperativeHandle(ref, () => ({
    play: showPlayerVisible
  }));

  const onPlaying = () => (
    onPlayingHandler({ videoRef })
  );

  const player = useMemo(() => (
    <div className={ classes.playerContent } ref={ playerContentRef }>
      <div
        className={ classes.reactPlayerContainer }
        style={ getReactPlayerContainerStyle() }
      >
        <button
          ref={ closeBtnRef }
          className={ classes.closeButton }
          type="button"
          style={ getCloseButtonTopStyle() }
        >
          <img src={ xIcon } alt="" className={ classes.closeImg } suppressHydrationWarning />
        </button>
        <VideoPlayer
          videoUrl={ profileVideoUrl }
          videoRef={ videoRef }
          onPlayerClick={ onPlayerClick }
          onPlaying={ onPlaying }
          muted={ muted }
        />
      </div>
    </div>
  ), [playerVisible, profileVideoUrl]);

  const renderPlayBtn = () => {
    const playBtn = getPlayBtn({ playImg });
    return (
      <div
        className={ classes.playBtnContainer }
        onClick={ showPlayerVisible }
      >
        {playBtn}
      </div>
    );
  };

  const renderAdvisorVideoPlayer = () => {
    if (!profileVideoUrl) return null;
    return (
      <>
        {renderPlayBtn()}
        {playerVisible && (
          <Popover
            isOpen={ playerVisible }
            className={ classes.videoPopoverContainer }
          >
            {player}
          </Popover>
        )}
      </>
    );
  };

  return renderAdvisorVideoPlayer();
});

export default AdvisorVideoPlayer;
